// Insurance product definitions used across landing pages and the quote funnel.
export const INSURANCE_OPTIONS = [
  "Auto", "Home", "Auto + Home Bundle", "Renters", "Motorcycle",
  "Commercial / Business", "Commercial Auto", "Life Insurance", "Other",
];

export const PRODUCTS = {
  auto: {
    key: "auto", type: "Auto", slug: "auto-insurance",
    headline: "PAYING TOO MUCH FOR CAR INSURANCE?",
    sub: "Let Keystone Shield Insurance help you compare coverage options and find a policy that works for your budget.",
    cta: "COMPARE MY OPTIONS",
    image: "https://images.pexels.com/photos/15549900/pexels-photo-15549900.jpeg",
    intro: "Auto coverage protects you on every Pennsylvania road. We shop options so you don't overpay for the protection you need.",
    coverages: ["Liability", "Comprehensive", "Collision", "Uninsured/Underinsured Motorist", "Medical Benefits", "Roadside Assistance", "Rental Reimbursement"],
    discounts: ["Multi-Car Discounts", "Auto + Home Bundling", "Good Driver Discounts"],
    benefits: ["Compare multiple coverage options", "Personal local service", "No-obligation quotes", "Help understanding your limits"],
    faqs: [
      { q: "How fast can I get a quote?", a: "Start online in under two minutes — a representative follows up quickly." },
      { q: "Can I keep my current coverage limits?", a: "Yes. We can match or review your current limits and suggest options." },
      { q: "Do you offer discounts?", a: "Discounts vary by carrier and eligibility — we help you find the ones you qualify for." },
    ],
  },
  home: {
    key: "home", type: "Home", slug: "home-insurance",
    headline: "PROTECT YOUR HOME. PROTECT WHAT YOU'VE BUILT.",
    sub: "Homeowners coverage tailored to your property and budget from a local Pennsylvania agency.",
    cta: "GET MY HOME + AUTO QUOTE",
    image: "https://images.pexels.com/photos/6510950/pexels-photo-6510950.jpeg",
    intro: "Your home is likely your biggest investment. We help you protect the structure, your belongings, and your liability.",
    coverages: ["Homeowners", "Dwelling Coverage", "Personal Property", "Liability", "Additional Living Expenses", "Other Structures"],
    discounts: ["Home + Auto Bundling", "Protective Device Discounts", "Claims-Free Discounts"],
    benefits: ["Coverage built around your property", "Bundle to potentially save", "Local claims support", "Clear, no-pressure guidance"],
    faqs: [
      { q: "Should I bundle home and auto?", a: "Bundling could save you money — ask us to compare bundled options." },
      { q: "What if I have a home business?", a: "We can review whether you need additional commercial coverage." },
      { q: "Is flood covered?", a: "Flood is typically separate — we can discuss your options." },
    ],
    bundlePromo: true,
  },
  bundle: {
    key: "bundle", type: "Auto + Home Bundle", slug: "auto-home-bundle",
    headline: "BUNDLE & SAVE. HOME + AUTO, ONE AGENCY.",
    sub: "Combine your home and auto coverage and let us shop it as one. See if bundling could save you money.",
    cta: "GET MY BUNDLE QUOTE",
    image: "https://images.pexels.com/photos/6267583/pexels-photo-6267583.jpeg",
    intro: "One agency, one point of contact, coverage that works together. Bundling is one of the easiest ways to simplify and potentially save.",
    coverages: ["Home Dwelling & Property", "Auto Liability & Physical Damage", "Combined Liability", "Single Point of Service"],
    discounts: ["Multi-Policy Bundling", "Multi-Car Discounts", "Loyalty Options"],
    benefits: ["Potential multi-policy savings", "One agency to manage everything", "Simplified renewals", "Personal local service"],
    faqs: [
      { q: "Do I have to switch both at once?", a: "No — but bundling is where many customers see the most value." },
      { q: "Can you match my current coverage?", a: "Yes, we review your current policies and quote comparable options." },
    ],
  },
  renters: {
    key: "renters", type: "Renters", slug: "renters-insurance",
    headline: "AFFORDABLE PROTECTION FOR RENTERS.",
    sub: "Protect your belongings and liability without breaking the bank.",
    cta: "GET MY RENTERS QUOTE",
    image: "https://images.pexels.com/photos/6267583/pexels-photo-6267583.jpeg",
    intro: "Renters insurance is often more affordable than people expect and protects what your landlord's policy doesn't.",
    coverages: ["Personal Property", "Liability", "Additional Living Expenses", "Medical Payments"],
    discounts: ["Auto + Renters Bundling", "Protective Device Discounts"],
    benefits: ["Affordable monthly options", "Protects your belongings", "Liability protection", "Fast local service"],
    faqs: [
      { q: "Is renters insurance required?", a: "Some landlords require it — and it's smart protection either way." },
      { q: "Does it cover theft away from home?", a: "Many policies extend coverage — we'll review the details with you." },
    ],
  },
  motorcycle: {
    key: "motorcycle", type: "Motorcycle", slug: "motorcycle-insurance",
    headline: "RIDE COVERED. RIDE CONFIDENT.",
    sub: "Coverage built for your bike and the way you ride.",
    cta: "GET MY MOTORCYCLE QUOTE",
    image: "https://images.pexels.com/photos/16975390/pexels-photo-16975390.jpeg",
    intro: "From weekend cruisers to daily riders, we help you find coverage that fits your bike and budget.",
    coverages: ["Liability", "Comprehensive", "Collision", "Uninsured Motorist", "Accessory Coverage", "Roadside Assistance"],
    discounts: ["Multi-Vehicle Discounts", "Rider Safety Course Discounts"],
    benefits: ["Coverage for your bike & gear", "Seasonal options", "Local service", "No-obligation quotes"],
    faqs: [
      { q: "Can I insure a custom bike?", a: "Yes — tell us about your accessories and we'll factor them in." },
      { q: "Do you cover seasonal riders?", a: "We can discuss options that fit riding season needs." },
    ],
  },
  commercial: {
    key: "commercial", type: "Commercial / Business", slug: "commercial-insurance",
    headline: "YOUR BUSINESS WORKS HARD. MAKE SURE IT'S PROTECTED.",
    sub: "Comprehensive business insurance from a local agency that understands Pennsylvania businesses.",
    cta: "GET A BUSINESS INSURANCE QUOTE",
    image: "https://images.pexels.com/photos/5920775/pexels-photo-5920775.jpeg",
    intro: "Protect your operation, property, employees, and reputation with coverage tailored to your industry.",
    coverages: ["General Liability", "Commercial Property", "Business Owners Policy", "Commercial Auto", "Workers Compensation", "Landlord / Lessor's Risk", "Professional Liability", "Contractor Insurance", "Retail Business Insurance", "Restaurant Insurance", "Other Business Coverage"],
    discounts: ["Bundled Business Policies", "Claims-Free Options"],
    benefits: ["Industry-specific coverage", "Dedicated local agent", "Upload documents securely", "Review current coverage"],
    faqs: [
      { q: "Can you review my current policy?", a: "Yes — upload it and we'll help compare coverage and cost." },
      { q: "Do you cover contractors?", a: "We work with many trades and business types across PA." },
    ],
    commercial: true,
  },
  "commercial-auto": {
    key: "commercial-auto", type: "Commercial Auto", slug: "commercial-auto-insurance",
    headline: "KEEP YOUR FLEET MOVING.",
    sub: "Commercial auto coverage for vehicles that keep your business running.",
    cta: "GET A COMMERCIAL AUTO QUOTE",
    image: "https://images.pexels.com/photos/16975390/pexels-photo-16975390.jpeg",
    intro: "Whether it's one work truck or an entire fleet, we help protect your vehicles, drivers, and cargo.",
    coverages: ["Liability", "Physical Damage", "Hired & Non-Owned Auto", "Cargo", "Medical Payments", "Uninsured Motorist"],
    discounts: ["Fleet Discounts", "Bundled Business Policies"],
    benefits: ["Coverage for work vehicles", "Fleet options", "Local claims support", "Review current coverage"],
    faqs: [
      { q: "Can you cover multiple vehicles?", a: "Yes — fleets of any size. Tell us about your vehicles." },
      { q: "Do you cover employee drivers?", a: "We'll review driver and coverage details with you." },
    ],
    commercial: true,
  },
  life: {
    key: "life", type: "Life Insurance", slug: "life-insurance",
    headline: "PROTECT THE PEOPLE WHO DEPEND ON YOU.",
    sub: "Life insurance options explained in plain language, with no pressure.",
    cta: "GET A FREE LIFE INSURANCE QUOTE",
    image: "https://images.pexels.com/photos/6888761/pexels-photo-6888761.jpeg",
    intro: "The right life insurance gives your family financial security. We'll help you understand your options.",
    coverages: ["Term Life", "Whole Life", "Permanent Life", "Final Expense", "Family Protection"],
    discounts: [],
    benefits: ["Plain-language guidance", "Options for every stage of life", "No-obligation review", "Local personal service"],
    faqs: [
      { q: "What's the difference between term and whole life?", a: "Term covers a set period; whole life is permanent with cash value. We'll explain both." },
      { q: "Do I need a medical exam?", a: "It depends on the product and carrier — some options require no exam." },
    ],
    life: true,
    disclosure: "Coverage, eligibility, rates, and approval are determined by the insurance carrier and are not guaranteed.",
  },
  other: {
    key: "other", type: "Other", slug: "other-insurance",
    headline: "NEED SOMETHING ELSE? WE CAN HELP.",
    sub: "Umbrella, boat, RV, and specialty coverage — tell us what you need.",
    cta: "REQUEST A QUOTE",
    image: "https://images.pexels.com/photos/37410126/pexels-photo-37410126.jpeg",
    intro: "As an independent agency we can help with a wide range of coverage needs. Tell us what you're looking for.",
    coverages: ["Umbrella", "Boat / Watercraft", "RV", "Landlord", "Specialty / Other"],
    discounts: ["Multi-Policy Bundling"],
    benefits: ["Access to multiple carriers", "Personal guidance", "No-obligation quotes", "Local service"],
    faqs: [
      { q: "What if my need isn't listed?", a: "Just tell us in the form — we'll point you in the right direction." },
    ],
  },
};

export const PRODUCT_LIST = Object.values(PRODUCTS);

// map a homepage insurance selection to a funnel flow
export function flowFor(types) {
  const t = (types || []).join(" ").toLowerCase();
  if (t.includes("commercial") || t.includes("business")) return "commercial";
  if (t.includes("life")) return "life";
  if (t.includes("home") || t.includes("renter")) return "home";
  if (t.includes("auto") || t.includes("motorcycle")) return "auto";
  return "auto";
}
